import { Link } from "react-router-dom";
import { NuvraLogo } from "@/components/NuvraLogo";
import { Github, Twitter, Linkedin, Users } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { getConfiguredSocialLinks } from "@/config/socialLinks";

const footerSections = [
  {
    titleKey: "footer.section.platform",
    links: [
      { labelKey: "footer.link.features",   href: "/#features",     scroll: true },
      { labelKey: "footer.link.howItWorks", href: "/#how-it-works", scroll: true },
      { labelKey: "footer.link.useCases",   href: "/#use-cases",    scroll: true },
      { labelKey: "footer.link.liveDemo",   href: "/#system-architecture", scroll: true },
    ],
  },
  {
    titleKey: "footer.section.product",
    links: [
      { labelKey: "footer.link.dashboard", href: "/dashboard"  },
      { labelKey: "footer.link.deploy",    href: "/deploy"     },
      { labelKey: "footer.link.billing",   href: "/billing"    },
    ],
  },
  {
    titleKey: "footer.section.legal",
    links: [
      { labelKey: "footer.link.privacyPolicy",  href: "/privacy"          },
      { labelKey: "footer.link.termsOfService", href: "/terms"            },
      { labelKey: "footer.link.acceptableUse",  href: "/acceptable-use"   },
      { labelKey: "footer.link.aiContentPolicy",href: "/ai-content"       },
      { labelKey: "footer.link.securityPolicy", href: "/security-policy"  },
    ],
  },
];

// Icons only for profiles that actually exist: the list comes from
// src/config/socialLinks.ts and is empty until real URLs are configured,
// so no dead "#" buttons ever ship.
const SOCIAL_ICONS = {
  github: Github,
  x: Twitter,
  linkedin: Linkedin,
  community: Users,
} as const;

const socialLinks = getConfiguredSocialLinks();

export function Footer() {
  const { t } = useI18n();
  return (
    <footer className="border-t border-border/40 bg-card/30 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Main footer grid */}
        <div className="py-12 sm:py-16 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand column */}
          <div className="col-span-2 sm:col-span-3 lg:col-span-1 space-y-4">
            <div className="flex items-center gap-2.5">
              <NuvraLogo size={28} decorative />
              <span className="text-lg font-bold text-foreground">Nuvra</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
              Describe your system. Nuvra generates, orchestrates, and ships it to production.
            </p>
            {socialLinks.length > 0 && (
              <div className="flex items-center gap-3 pt-1">
                {socialLinks.map((s) => {
                  const Icon = SOCIAL_ICONS[s.id];
                  return (
                    <a
                      key={s.id}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.label}
                      className="flex items-center justify-center h-8 w-8 rounded-lg border border-border/50 bg-secondary/40 text-muted-foreground hover:text-foreground hover:border-primary/40 hover:bg-primary/10 transition-all"
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Link columns */}
          {footerSections.map((section) => (
            <div key={section.titleKey} className="space-y-3">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-foreground">
                {t(section.titleKey)}
              </h3>
              <ul className="space-y-2.5">
                {section.links.map((link) => (
                  <li key={link.labelKey}>
                    {"scroll" in link && link.scroll ? (
                      <button
                        onClick={() => {
                          const hash = link.href.split("#")[1];
                          if (hash) {
                            const el = document.getElementById(hash);
                            if (el) {
                              el.scrollIntoView({ behavior: "smooth" });
                            } else {
                              window.location.href = link.href;
                            }
                          }
                        }}
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors text-left"
                      >
                        {t(link.labelKey)}
                      </button>
                    ) : (
                      <Link
                        to={link.href}
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {t(link.labelKey)}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="border-t border-border/40 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Nuvra. {t("footer.rightsReserved")}
          </p>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <Link to="/privacy" className="hover:text-foreground transition-colors">{t("footer.bottom.privacy")}</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">{t("footer.bottom.terms")}</Link>
            <Link to="/cookie-policy" className="hover:text-foreground transition-colors">{t("footer.bottom.cookies")}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
